import React from "react";
import { motion } from "framer-motion";
import { WHATSAPP_URL } from "@/lib/constants";

const EASE_OUT_EXPO = [0.22, 1, 0.36, 1];

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

const HeroBackground = () => (
    <div className="absolute inset-0">
        <img
            src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=2400&q=80"
            alt="Interior moderno e minimalista de clínica odontológica de alto padrão"
            className="h-full w-full object-cover"
            loading="eager"
        />
        {/* Multi-layer overlay for elegant depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-navy/85 via-navy/65 to-navy/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-onyx/70 via-transparent to-transparent" />
    </div>
);

const HeroEyebrow = () => (
    <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.1, ease: EASE_OUT_EXPO }}
        className="flex items-center gap-4 font-sans text-[11px] font-medium uppercase tracking-widestx text-gold-400 md:text-xs"
    >
        <span className="inline-block h-px w-12 bg-gold-500" />
        Excelência em Implantodontia
    </motion.p>
);

const HeroHeadline = () => (
    <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 0.25, ease: EASE_OUT_EXPO }}
        className="mt-8 font-serif text-4xl font-medium leading-[1.08] text-white text-balance sm:text-5xl md:text-6xl lg:text-[68px]"
    >
        Reabilitação Oral Definitiva:{" "}
        <span className="italic text-gold-300">Estética, Função</span> e
        Previsibilidade.
    </motion.h1>
);

const HeroSubheadline = () => (
    <motion.p
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.45, ease: EASE_OUT_EXPO }}
        className="mt-8 max-w-xl font-sans text-base font-light leading-relaxed text-white/80 md:text-lg"
    >
        Recupere a segurança do seu sorriso através de um planejamento
        cirúrgico 100% digital.
    </motion.p>
);

const PrimaryCta = () => (
    <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="hero-cta"
        className="group relative inline-flex items-center justify-center gap-3 overflow-hidden bg-gold-500 px-8 py-5 font-sans text-xs font-semibold uppercase tracking-widestx text-white shadow-gold transition-all duration-500 hover:bg-gold-600 md:text-[13px]"
    >
        <span className="relative z-10">
            Agendar Avaliação com o Especialista
        </span>
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            className="relative z-10 h-4 w-4 transition-transform duration-500 group-hover:translate-x-1"
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 12h14M13 5l7 7-7 7"
            />
        </svg>
        <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
    </a>
);

const AudienceTag = () => (
    <div className="flex items-center gap-3 text-white/70">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="h-4 w-4 text-gold-400"
                aria-hidden="true"
            >
                <path d="M12 2.5l2.39 6.94 7.32.27-5.86 4.4 2.15 7.02L12 16.97l-6 4.16 2.15-7.02-5.86-4.4 7.32-.27L12 2.5z" />
            </svg>
        </span>
        <p className="font-sans text-[11px] uppercase tracking-widestx text-white/60 md:text-xs">
            Atendimento Particular
            <br className="hidden sm:block" />
            <span className="text-white/45 normal-case tracking-normal">
                Casos de média e alta complexidade
            </span>
        </p>
    </div>
);

const HeroActions = () => (
    <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.65, ease: EASE_OUT_EXPO }}
        className="mt-12 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
    >
        <PrimaryCta />
        <AudienceTag />
    </motion.div>
);

const ScrollCue = () => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/60 md:flex"
        aria-hidden="true"
    >
        <span className="font-sans text-[10px] uppercase tracking-widestx">
            Descer
        </span>
        <span className="relative h-12 w-px overflow-hidden bg-white/20">
            <span className="absolute inset-x-0 top-0 h-4 animate-[fade-up_1.6s_ease-in-out_infinite] bg-gold-500" />
        </span>
    </motion.div>
);

/* -------------------------------------------------------------------------- */
/*  Hero                                                                      */
/* -------------------------------------------------------------------------- */

const Hero = () => (
    <section
        id="top"
        data-testid="hero-section"
        className="relative isolate min-h-[100svh] w-full overflow-hidden bg-onyx"
    >
        <HeroBackground />

        <div className="relative z-10 flex min-h-[100svh] items-center">
            <div className="container-luxe w-full pt-32 pb-24 md:pt-40 md:pb-32">
                <div className="max-w-3xl">
                    <HeroEyebrow />
                    <HeroHeadline />
                    <HeroSubheadline />
                    <HeroActions />
                </div>
            </div>
        </div>

        <ScrollCue />
    </section>
);

export default Hero;
